import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { useArticles } from "../../hooks/useArticles";
import { toNewsItems } from "../../lib/articleAdapter";
import SEO from "@/components/SEO";

const months = [
	"Enero",
	"Febrero",
	"Marzo",
	"Abril",
	"Mayo",
	"Junio",
	"Julio",
	"Agosto",
	"Septiembre",
	"Octubre",
	"Noviembre",
	"Diciembre",
];

export default function Archivo() {
	const { articles, loading } = useArticles();
	const allItems = toNewsItems(articles).filter(
		(item) => item.category !== "Técnica" && item.category !== "Curiosidades",
	);

	const [selectedYear, setSelectedYear] = useState<string | null>(null);
	const [selectedMonth, setSelectedMonth] = useState<number | null>(null);
	const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

	// Extract unique years from articles
	const years = useMemo(() => {
		const y = new Set<string>();
		allItems.forEach((item) => {
			const parts = item.date.split(" ");
			if (parts.length >= 3) {
				y.add(parts[2]);
			}
		});
		return Array.from(y).sort().reverse();
	}, [allItems]);

	// Extract unique categories
	const categories = useMemo(() => {
		const c = new Set<string>();
		allItems.forEach((item) => c.add(item.category));
		return Array.from(c).sort();
	}, [allItems]);

	// Group articles by year and month
	const grouped = useMemo(() => {
		const g: Record<string, Record<number, typeof allItems>> = {};
		allItems.forEach((item) => {
			const parts = item.date.split(" ");
			if (parts.length >= 3) {
				const year = parts[2];
				const monthIdx = months.findIndex((m) =>
					m.toLowerCase().startsWith(parts[1].toLowerCase()),
				);
				if (!g[year]) g[year] = {};
				if (!g[year][monthIdx]) g[year][monthIdx] = [];
				g[year][monthIdx].push(item);
			}
		});
		return g;
	}, [allItems]);

	// Filtered articles
	const filtered = useMemo(() => {
		let result = allItems;
		if (selectedYear) {
			result = result.filter((item) => item.date.includes(selectedYear));
		}
		if (selectedMonth !== null) {
			result = result.filter((item) => {
				const parts = item.date.split(" ");
				const monthIdx = months.findIndex((m) =>
					m.toLowerCase().startsWith(parts[1].toLowerCase()),
				);
				return monthIdx === selectedMonth;
			});
		}
		if (selectedCategory) {
			result = result.filter(
				(item) =>
					(Array.isArray(item.tags)
						? item.tags.includes(selectedCategory)
						: false) || item.category === selectedCategory,
			);
		}
		return result;
	}, [allItems, selectedYear, selectedMonth, selectedCategory]);

	const totalArticles = allItems.length;

	return (
		<main
			className="min-h-screen bg-[#F4F7FB]"
			style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
		>
			<SEO
				jsonLdSchemas={[
					{
						"@type": "CollectionPage",
						name: "Archivo de Noticias",
						description: "Archivo completo de noticias aeronáuticas publicadas en Aviación Insight RD.",
						url: 'https://aviacioninsightrd.com/archivo',
					},
				]}
			/>
			<Navbar />

			{/* Hero */}
			<section className="bg-[#0B1F3A] pt-32 pb-16">
				<div className="max-w-screen-xl mx-auto px-4 md:px-6">
					<div className="flex items-center gap-2 mb-4">
						<Link
							to="/"
							className="text-blue-400 text-sm hover:text-blue-300 cursor-pointer"
						>
							Inicio
						</Link>
						<i className="ri-arrow-right-s-line text-blue-600" />
						<span className="text-slate-400 text-sm">Archivo</span>
					</div>
					<h1
						className="text-white font-bold mb-3"
						style={{
							fontFamily: "'Sora', sans-serif",
							fontSize: "clamp(2rem, 4vw, 3rem)",
						}}
					>
						ARCHIVO DE NOTICIAS
					</h1>
					<p className="text-blue-200/70 text-sm max-w-xl">
						Explora todo el contenido publicado en Aviación Insight RD,
						organizado por año, mes y categoría.
					</p>
					<div className="flex items-center gap-6 mt-6">
						<div className="text-center">
							<p
								className="text-white font-extrabold text-2xl"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								{totalArticles}
							</p>
							<p className="text-blue-300/60 text-xs">Artículos totales</p>
						</div>
						<div className="text-center">
							<p
								className="text-white font-extrabold text-2xl"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								{years.length}
							</p>
							<p className="text-blue-300/60 text-xs">Años de historia</p>
						</div>
						<div className="text-center">
							<p
								className="text-white font-extrabold text-2xl"
								style={{ fontFamily: "'Sora', sans-serif" }}
							>
								{categories.length}
							</p>
							<p className="text-blue-300/60 text-xs">Categorías</p>
						</div>
					</div>
				</div>
			</section>

			{/* Filters */}
			<section className="bg-white border-b border-slate-100 py-4 sticky top-0 z-30">
				<div className="max-w-screen-xl mx-auto px-4 md:px-6">
					<div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
						<span className="text-slate-400 text-xs font-semibold uppercase tracking-wider flex-shrink-0">
							<i className="ri-filter-3-line mr-1" />
							Filtrar:
						</span>

						{/* Year filter */}
						<div className="flex flex-wrap gap-2">
							<button
								onClick={() => {
									setSelectedYear(null);
									setSelectedMonth(null);
								}}
								className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
									!selectedYear
										? "bg-[#0B1F3A] text-white"
										: "bg-slate-100 text-slate-500 hover:bg-slate-200"
								}`}
							>
								Todos los años
							</button>
							{years.map((year) => (
								<button
									key={year}
									onClick={() => {
										setSelectedYear(year === selectedYear ? null : year);
										setSelectedMonth(null);
									}}
									className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
										selectedYear === year
											? "bg-[#0B1F3A] text-white"
											: "bg-slate-100 text-slate-500 hover:bg-slate-200"
									}`}
								>
									{year}
								</button>
							))}
						</div>

						{/* Month filter (only when year selected) */}
						{selectedYear && (
							<div className="flex flex-wrap gap-2 ml-0 sm:ml-4">
								<button
									onClick={() => setSelectedMonth(null)}
									className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
										selectedMonth === null
											? "bg-emerald-600 text-white"
											: "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
									}`}
								>
									Todo el año
								</button>
								{Object.keys(grouped[selectedYear] || {})
									.map(Number)
									.sort((a, b) => b - a)
									.map((monthIdx) => (
										<button
											key={monthIdx}
											onClick={() =>
												setSelectedMonth(
													monthIdx === selectedMonth ? null : monthIdx,
												)
											}
											className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
												selectedMonth === monthIdx
													? "bg-emerald-600 text-white"
													: "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
											}`}
										>
											{months[monthIdx]}
										</button>
									))}
							</div>
						)}

						{/* Category filter */}
						<div className="flex flex-wrap gap-2 ml-0 sm:ml-auto">
							<button
								onClick={() => setSelectedCategory(null)}
								className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
									!selectedCategory
										? "bg-amber-500 text-white"
										: "bg-amber-50 text-amber-600 hover:bg-amber-100"
								}`}
							>
								Todas
							</button>
							{categories.map((cat) => (
								<button
									key={cat}
									onClick={() =>
										setSelectedCategory(cat === selectedCategory ? null : cat)
									}
									className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer whitespace-nowrap ${
										selectedCategory === cat
											? "bg-amber-500 text-white"
											: "bg-amber-50 text-amber-600 hover:bg-amber-100"
									}`}
								>
									{cat}
								</button>
							))}
						</div>
					</div>

					{/* Active filters summary */}
					{(selectedYear || selectedMonth !== null || selectedCategory) && (
						<div className="flex items-center gap-2 mt-3 pt-3 border-t border-slate-100">
							<span className="text-slate-400 text-xs">Filtros activos:</span>
							{selectedYear && (
								<span className="inline-flex items-center gap-1 px-2 py-1 bg-[#0B1F3A]/10 text-[#0B1F3A] text-[10px] font-bold rounded-md">
									{selectedYear}
									<button
										onClick={() => {
											setSelectedYear(null);
											setSelectedMonth(null);
										}}
										className="cursor-pointer hover:text-red-500"
									>
										<i className="ri-close-line" />
									</button>
								</span>
							)}
							{selectedMonth !== null && (
								<span className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 text-[10px] font-bold rounded-md">
									{months[selectedMonth]}
									<button
										onClick={() => setSelectedMonth(null)}
										className="cursor-pointer hover:text-red-500"
									>
										<i className="ri-close-line" />
									</button>
								</span>
							)}
							{selectedCategory && (
								<span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-[10px] font-bold rounded-md">
									{selectedCategory}
									<button
										onClick={() => setSelectedCategory(null)}
										className="cursor-pointer hover:text-red-500"
									>
										<i className="ri-close-line" />
									</button>
								</span>
							)}
							<button
								onClick={() => {
									setSelectedYear(null);
									setSelectedMonth(null);
									setSelectedCategory(null);
								}}
								className="text-red-500 text-[10px] font-bold hover:underline cursor-pointer ml-2"
							>
								Limpiar todo
							</button>
						</div>
					)}
				</div>
			</section>

			{/* Results */}
			<section className="py-10">
				<div className="max-w-screen-xl mx-auto px-4 md:px-6">
					{loading ? (
						<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
							{[1, 2, 3, 4, 5, 6].map((i) => (
								<div
									key={i}
									className="h-80 bg-slate-200 rounded-2xl animate-pulse"
								/>
							))}
						</div>
					) : filtered.length === 0 ? (
						<div className="text-center py-20">
							<div className="w-16 h-16 flex items-center justify-center bg-slate-100 rounded-full mx-auto mb-4">
								<i className="ri-search-line text-slate-400 text-2xl" />
							</div>
							<p className="text-slate-500 text-sm font-semibold">
								No se encontraron artículos con estos filtros.
							</p>
							<button
								onClick={() => {
									setSelectedYear(null);
									setSelectedMonth(null);
									setSelectedCategory(null);
								}}
								className="mt-4 px-5 py-2 bg-[#0B1F3A] text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap"
							>
								Ver todos los artículos
							</button>
						</div>
					) : (
						<>
							<p
								className="text-slate-400 text-xs mb-6"
								style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
							>
								Mostrando{" "}
								<strong className="text-[#0B1F3A]">{filtered.length}</strong>{" "}
								artículo{filtered.length !== 1 ? "s" : ""}
							</p>
							<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
								{filtered.map((item) => (
									<Link
										key={item.id}
										to={`/articulo/${item.slug}`}
										className="bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-slate-200 transition-all hover:-translate-y-1 duration-300 group cursor-pointer block"
									>
										<div className="h-48 overflow-hidden bg-[#0B1F3A]">
											<img
												src={item.image}
												alt={item.title}
												className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
											/>
										</div>
										<div className="p-5">
											<div className="flex items-center gap-2 mb-2">
												<span
													className={`inline-block text-white text-[9px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full ${item.categoryColor}`}
												>
													{item.category}
												</span>
												<span className="text-slate-400 text-[10px]">
													{item.date}
												</span>
											</div>
											<h3
												className="text-[#0B1F3A] font-bold text-sm leading-snug line-clamp-2 group-hover:text-slate-600 transition-colors mb-2"
												style={{ fontFamily: "'Sora', sans-serif" }}
											>
												{item.title}
											</h3>
											<p
												className="text-slate-400 text-xs line-clamp-2 leading-relaxed"
												style={{
													fontFamily: "'Plus Jakarta Sans', sans-serif",
												}}
											>
												{item.excerpt}
											</p>
										</div>
									</Link>
								))}
							</div>
						</>
					)}
				</div>
			</section>

			<Footer />
		</main>
	);
}
